import type { OfficeItemMeta, OutlookMailSession } from "@src/load-agent/types/office";

// Persists internetMessageId → EWS itemId across the add-in session.
// Populated by getItemMeta() each time the user selects an email.
const ewsItemIdCache = new Map<string, string>();

type OutlookItem = {
  subject?: string;
  dateTimeCreated?: string | Date;
  internetMessageId?: string;
  conversationId?: string;
  /**
   * Message compose: parent message internet message id (Outlook Mailbox 1.14+).
   * Unsent drafts usually have no `internetMessageId` but replies often have this.
   */
  inReplyTo?: string | null;
};

function extractHeaderValue(headers: string, headerName: string): string {
  const lines = headers.split(/\r?\n/);
  let i = 0;
  const prefix = new RegExp(`^${headerName}:\\s*(.*)$`, "i");

  while (i < lines.length) {
    const m = lines[i].match(prefix);

    if (m) {
      let val = m[1] ?? "";

      i++;
      while (i < lines.length && /^[ \t]/.test(lines[i])) {
        val += " " + lines[i].trim();
        i++;
      }
      return val.trim();
    }
    i++;
  }
  return "";
}

export class OfficeJsHost implements OutlookMailSession {
  async onReady(): Promise<void> {
    await Office.onReady();
  }
  onItemChanged(handler: () => void): (() => void) | null {
    const mailbox = Office?.context?.mailbox as
      | (Office.Mailbox & {
          addHandlerAsync?: (
            eventType: Office.EventType,
            handler: () => void,
            callback?: (result: Office.AsyncResult<void>) => void
          ) => void;
          removeHandlerAsync?: (
            eventType: Office.EventType,
            options?: {
              handler?: () => void;
            },
            callback?: (result: Office.AsyncResult<void>) => void
          ) => void;
        })
      | undefined;

    if (
      !mailbox ||
      typeof mailbox.addHandlerAsync !== "function" ||
      typeof Office?.EventType?.ItemChanged === "undefined"
    ) {
      return null;
    }
    mailbox.addHandlerAsync(Office.EventType.ItemChanged, handler);
    return () => {
      if (typeof mailbox.removeHandlerAsync === "function") {
        mailbox.removeHandlerAsync(Office.EventType.ItemChanged, { handler });
      }
    };
  }
  getItemMeta(): OfficeItemMeta | null {
    const item = (Office?.context?.mailbox?.item ?? null) as
      | (OutlookItem & {
          itemId?: string;
        })
      | null;

    if (!item) return null;
    const subject = item.subject ?? "";
    const createdDate = item.dateTimeCreated ? new Date(item.dateTimeCreated) : null;
    const rawItemId =
      typeof item.internetMessageId === "string" ? item.internetMessageId.trim() : "";
    const rawInReply = typeof item.inReplyTo === "string" ? item.inReplyTo.trim() : "";
    const internetMessageId = rawItemId || rawInReply || null;
    const rawConv = typeof item.conversationId === "string" ? item.conversationId.trim() : "";
    const conversationId = rawConv || null;
    const ewsItemId =
      typeof item.itemId === "string" && item.itemId.trim() ? item.itemId.trim() : null;

    // Cache the EWS item ID so the dashboard navigate button can use it without EWS permissions
    if (internetMessageId && ewsItemId) {
      ewsItemIdCache.set(internetMessageId, ewsItemId);
    }

    return { subject, createdDate, internetMessageId, conversationId, ewsItemId };
  }
  async getThreadMessageIdCandidatesFromHeaders(): Promise<string[]> {
    const headers = await this.loadAllInternetHeadersString();

    if (!headers) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    const pushFromBlob = (blob: string) => {
      const matches = blob.match(/<[^>]+>/g);

      if (!matches) return;
      for (const m of matches) {
        const id = m.trim();

        if (!seen.has(id)) {
          seen.add(id);
          out.push(id);
        }
      }
    };

    pushFromBlob(extractHeaderValue(headers, "References"));
    pushFromBlob(extractHeaderValue(headers, "In-Reply-To"));
    return out;
  }
  async getOriginalInternetMessageIdFromHeaders(): Promise<string | null> {
    const c = await this.getThreadMessageIdCandidatesFromHeaders();

    return c[0] ?? null;
  }
  async displayEmailByEwsId(ewsItemId: string): Promise<{ ok: boolean; error?: string }> {
    const mailbox = Office?.context?.mailbox as
      | (Office.Mailbox & {
          displayMessageFormAsync?: (
            itemId: string,
            options: Record<string, unknown>,
            callback: (result: Office.AsyncResult<void>) => void
          ) => void;
        })
      | undefined;

    if (!mailbox) {
      return { ok: false, error: "Outlook mailbox not available." };
    }
    if (typeof mailbox.displayMessageFormAsync === "function") {
      return new Promise((resolve) => {
        mailbox.displayMessageFormAsync!(ewsItemId, {}, (r) => {
          if (r.status === Office.AsyncResultStatus.Succeeded) {
            resolve({ ok: true });
          } else {
            resolve({ ok: false, error: String(r.error?.message ?? "Failed to open message") });
          }
        });
      });
    }
    // Synchronous fallback for older Outlook versions
    (mailbox as unknown as { displayMessageForm(id: string): void }).displayMessageForm?.(
      ewsItemId
    );
    return { ok: true };
  }
  async displayEmailByInternetMessageId(
    internetMessageId: string
  ): Promise<{ ok: boolean; error?: string }> {
    const cachedEwsId = ewsItemIdCache.get(internetMessageId);

    if (!cachedEwsId) {
      return {
        ok: false,
        error: "Open this email in Outlook first to enable navigation from the dashboard.",
      };
    }
    return this.displayEmailByEwsId(cachedEwsId);
  }
  private async loadAllInternetHeadersString(): Promise<string | null> {
    const mailbox = Office?.context?.mailbox;
    const item = mailbox?.item as
      | (OutlookItem & {
          getAllInternetHeadersAsync?: (
            callback: (result: Office.AsyncResult<string>) => void
          ) => void;
        })
      | null;

    if (!item || typeof item.getAllInternetHeadersAsync !== "function") {
      return null;
    }
    const supportsHeaders =
      typeof Office.context.requirements?.isSetSupported === "function"
        ? Office.context.requirements.isSetSupported("Mailbox", "1.8")
        : true;

    if (!supportsHeaders) {
      return null;
    }
    return await new Promise<string | null>((resolve) => {
      item.getAllInternetHeadersAsync?.((asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded || !asyncResult.value) {
          resolve(null);
          return;
        }
        resolve(asyncResult.value as string);
      });
    });
  }
}
